import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { CommandBar } from "components/Layout/Campaign/Common/CampaignRules/Rule/CommandBar/CommandBar";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import {
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignConditionType,
  CampaignTriggersUnion,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { alphabetizeByProperty } from "utilities/arrays";
import { Condition, ConditionError } from "./Condition";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    buttonAdd: {
      lineHeight: "24px",
      margin: theme.spacing(8, 8),
      padding: theme.spacing(2, 8),
    },
    conditionWrapper: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
    },
    conditionHeader: {
      fontSize: 14,
      fontWeight: 500,
      margin: theme.spacing(8),
    },
  }),
);

export const Conditions = (props: {
  conditions: (
    | CampaignCondition
    | SelectedSchemaCondition
    | CampaignConditionNewFormat
  )[];
  hideHeader?: boolean;
  ruleIndex: number;
  status?: any;
  rule: any;
  error?: {
    [key: number]: string;
  };
  trigger?: CampaignTriggersUnion;
  transactionItemsFilters?: TransactionItem[];
}) => {
  const classes = useStyles();
  const { message } = useMessage();
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);
  const isRefferalCampaign = !!props.rule.target;
  const filteredConditions = (props.conditions as CampaignCondition[]).filter(
    (item) => !item.isDeprecated,
  );

  return (
    <>
      <Typography className={classes.conditionHeader}>
        {message({ id: "conditions" })}
      </Typography>
      <FieldArray
        name={`rules.[${props.ruleIndex}].conditions`}
        render={({
          remove,
          replace,
          insert,
          unshift,
          form: { values, setFieldValue, setFieldError },
        }) => {
          const { rules } = values;
          const areConditionsAdded =
            rules && rules[props.ruleIndex].conditions?.length > 0;

          return (
            <>
              <OLButton
                className={classes.buttonAdd}
                data-test-id="conditions-add"
                onClick={() => setIsCommandBarOpen(true)}
                variant="outlined"
                size="small"
                startIcon={<PlusIcon />}
              >
                {message({ id: "addCondition" })}
              </OLButton>
              {areConditionsAdded && (
                <div className={classes.conditionWrapper}>
                  {rules[props.ruleIndex]?.conditions?.map(
                    (
                      input: {
                        isEditing: boolean;
                        isSaved: boolean;
                        attribute: string;
                        type?: CampaignConditionType;
                        draft?: {
                          attribute: string;
                          type?: CampaignConditionType;
                        };
                      },
                      index: number,
                    ) => {
                      const conditionError = props.error && props.error[index];

                      return (
                        <Condition
                          key={index}
                          rule={rules?.[props.ruleIndex]}
                          condition={rules[props.ruleIndex]?.conditions[index]}
                          input={input.draft || input}
                          conditions={props.conditions}
                          index={index}
                          ruleIndex={props.ruleIndex}
                          remove={remove}
                          replace={replace}
                          setFieldValue={setFieldValue}
                          insert={insert}
                          setFieldError={setFieldError}
                          error={conditionError as ConditionError}
                          target={rules[props.ruleIndex]?.target}
                          transactionItemsFilters={
                            props.transactionItemsFilters
                          }
                        />
                      );
                    },
                  )}
                </div>
              )}
              <CommandBar
                isOpen={isCommandBarOpen}
                onClose={() => setIsCommandBarOpen(false)}
                conditions={alphabetizeByProperty(filteredConditions, "label")}
                unshift={unshift}
                isRefferalCampaign={isRefferalCampaign}
                trigger={props.trigger}
                transactionItemsFilters={props.transactionItemsFilters}
              />
            </>
          );
        }}
      />
    </>
  );
};
